import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WASTE_FORM_INPUT_CLASS } from "../../settings/waste/wasteFlowFormUi";
import WasteFlowFormChevron from "../../settings/waste/WasteFlowFormChevron";

const getContainerLabel = (container, t) => {
  if (container?.positioning) {
    return container.positioning;
  }
  return `${t("Container")} #${container?.name ?? ""}`;
};

const CampaignZoneField = ({
  containers,
  selectedIds,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  const selectedContainers = useMemo(() => {
    return containers.filter((container) =>
      selectedIds.includes(container._id),
    );
  }, [containers, selectedIds]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleContainer = (containerId) => {
    onChange(
      selectedIds.includes(containerId)
        ? selectedIds.filter((id) => id !== containerId)
        : [...selectedIds, containerId],
    );
  };

  const removeContainer = (event, containerId) => {
    event.preventDefault();
    event.stopPropagation();
    onChange(selectedIds.filter((id) => id !== containerId));
  };

  return (
    <div
      className={`campaign-create-zone-field ${isOpen ? "campaign-create-zone-field--open" : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`${WASTE_FORM_INPUT_CLASS} campaign-create-zone-field__trigger !h-auto`}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        disabled={disabled || containers.length === 0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="campaign-create-zone-field__chips">
          {selectedContainers.length > 0 ? (
            selectedContainers.map((container) => (
              <span
                key={container._id}
                className="campaign-create-zone-field__chip"
              >
                {getContainerLabel(container, t)}
                <button
                  type="button"
                  className="campaign-create-zone-field__chip-remove"
                  onClick={(event) => removeContainer(event, container._id)}
                  aria-label={t("Remove")}
                >
                  ×
                </button>
              </span>
            ))
          ) : (
            <span className="campaign-create-zone-field__placeholder">
              {t("campaign.selectZonePlaceholder")}
            </span>
          )}
        </div>
        <WasteFlowFormChevron className="campaign-create-zone-field__chevron" />
      </button>

      {isOpen ? (
        <div className="campaign-create-zone-field__panel" role="listbox">
          {containers.length === 0 ? (
            <p className="campaign-create-zone-field__empty">
              {t("campaign.noZonesAvailable")}
            </p>
          ) : (
            containers.map((container) => {
              const isSelected = selectedIds.includes(container._id);
              return (
                <label
                  key={container._id}
                  className="campaign-create-zone-field__option"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleContainer(container._id)}
                  />
                  <span>
                    {getContainerLabel(container, t)}
                    {container?.wasteName ? ` (${container.wasteName})` : ""}
                  </span>
                </label>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
};

export default CampaignZoneField;
